/**
 * ajax 封装 —— 基于原生 fetch
 *
 * 用法:
 *   import ajax from '../../utils/ajax';
 *   const data = await ajax.get('/user/list', { page: 1 });
 *   const res  = await ajax.post('/user/add', { name: '张三' });
 *
 * 特性:
 *   - 自动拼接 baseURL
 *   - 自动加 token (需先 ajax.setTokenGetter(...))
 *   - GET 参数自动转 ?a=1&b=2
 *   - POST/PUT 自动 JSON.stringify
 *   - FormData 自动去掉 Content-Type 让浏览器设 boundary
 *   - 超时控制(默认 30s)
 *   - 统一错误类型 AjaxError(code + message + data)
 */

// —— 配置项 —— 按需修改 ——————————————————————————————

// 默认 baseURL:
//   - 优先读环境变量 REACT_APP_API_BASE;留空表示不加前缀,直接走相对路径
//   - 开发环境: devServer proxy 通过 bypass 识别 API 路径(/user/、/website/)并转发到后端
//   - 生产环境: 若需要 Nginx 反代,自行配 REACT_APP_API_BASE=/api
const BASE_URL =
  process.env.REACT_APP_API_BASE !== undefined
    ? process.env.REACT_APP_API_BASE
    : '';

// 默认请求头 / 超时
const defaultHeaders = {
  'Content-Type': 'application/json',
};
const DEFAULT_TIMEOUT = 30000;

// —— Token 注入 ————————————————————————————————————
// 默认空函数,在登录态接入后再用 setTokenGetter 替换
let getToken = () => '';
export const setTokenGetter = (fn) => {
  getToken = fn;
};

// —— 统一错误类型 ————————————————————————————————
export class AjaxError extends Error {
  constructor(message, code, data) {
    super(message);
    this.name = 'AjaxError';
    this.code = code; // HTTP 状态码或业务码
    this.data = data; // 后端返回的完整 body
  }
}

// —— 核心请求函数 ————————————————————————————————
async function request(url, options = {}) {
  const {
    method = 'GET',
    data, // body
    params, // query
    baseURL = BASE_URL,
    timeout = DEFAULT_TIMEOUT,
    headers: customHeaders = {},
    signal, // 外部传入的 AbortSignal,用于主动取消
    ...rest
  } = options;

  // 1. 拼接 URL
  let fullUrl = /^https?:\/\//i.test(url) ? url : `${baseURL}${url}`;

  // 2. GET / DELETE 自动追加 query string
  if (params && typeof params === 'object') {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null),
    ).toString();
    if (qs) fullUrl += (fullUrl.includes('?') ? '&' : '?') + qs;
  }

  // 3. 组装 headers
  const headers = { ...defaultHeaders, ...customHeaders };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  // 4. 处理 body
  let body;
  if (data !== undefined && data !== null) {
    if (data instanceof FormData) {
      body = data;
      // FormData 必须让浏览器自动加 boundary,不能手写 Content-Type
      delete headers['Content-Type'];
    } else {
      body = JSON.stringify(data);
    }
  }

  // 5. 超时控制 —— 用 AbortController 打断
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  // 如果调用方也传了 signal,任一 abort 都会触发请求终止
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener('abort', () => controller.abort());
  }

  try {
    const response = await fetch(fullUrl, {
      method,
      headers,
      body,
      signal: controller.signal,
      credentials: 'include', // 默认带 cookie,需要时再调
      ...rest,
    });
    clearTimeout(timeoutId);

    // 6. 解析响应 —— JSON 或 text
    const contentType = response.headers.get('content-type') || '';
    let result;
    if (contentType.includes('application/json')) {
      result = await response.json();
    } else {
      result = await response.text();
    }

    // 7. HTTP 层失败
    if (!response.ok) {
      const message =
        (result && typeof result === 'object' && result.message) ||
        `请求失败 (${response.status})`;
      throw new AjaxError(message, response.status, result);
    }

    return result;
  } catch (err) {
    clearTimeout(timeoutId);
    if (err.name === 'AbortError') {
      throw new AjaxError('请求超时或被取消', 408, null);
    }
    throw err;
  }
}

// —— 快捷方法 ————————————————————————————————————
// get(url, params?, options?) / post(url, data?, options?)
const ajax = {
  get: (url, params, options) =>
    request(url, { ...options, method: 'GET', params }),
  post: (url, data, options) =>
    request(url, { ...options, method: 'POST', data }),
  put: (url, data, options) =>
    request(url, { ...options, method: 'PUT', data }),
  delete: (url, params, options) =>
    request(url, { ...options, method: 'DELETE', params }),
  patch: (url, data, options) =>
    request(url, { ...options, method: 'PATCH', data }),
  upload: (url, formData, options) =>
    request(url, { ...options, method: 'POST', data: formData }),
  request,
  setTokenGetter,
  AjaxError,
};

export default ajax;